import { Component, ElementRef, EventEmitter, Output, ViewChild, computed, inject } from '@angular/core';
import { SmartBroadcastService } from '../../../../../services/smart-broadcast/smart-broadcast.service';
import { MAX_FILE_BYTES } from '../../../../../services/smart-broadcast/smart-broadcast.utils';
import {
  SPREADSHEET_ACCEPT,
  formatFileSize,
  isSpreadsheetFile,
} from '../../../../../../utils/utils';
import { formatCount } from '../../../../../../utils/sms';

interface UploadNote {
  tone: 'ok' | 'wait' | 'bad' | 'idle';
  text: string;
  more?: string;
}

/**
 * Step 1 — upload a CSV/XLSX sheet. Reads every value as text (long numbers
 * in full), lets the user pick a sheet when the workbook has several, and
 * says plainly what was found and fixed.
 */
@Component({
  selector: 'sb-upload-data',
  standalone: true,
  templateUrl: './upload-data.component.html',
  styleUrls: ['../../sb-shared.css', './upload-data.component.css'],
})
export class UploadDataComponent {
  @Output() next = new EventEmitter<void>();

  store = inject(SmartBroadcastService);

  @ViewChild('picker') picker?: ElementRef<HTMLInputElement>;

  loading = false;
  isHovering = false;
  errorMessage = '';
  readingName = '';
  /** Text for the step's status region: reading, then what was read. */
  announcement = '';
  readonly acceptedFormats = SPREADSHEET_ACCEPT;
  readonly formatCount = formatCount;

  get fileSize(): string {
    const bytes = this.store.fileSize();
    return bytes ? formatFileSize(bytes, 1) : '';
  }

  /** What the upload found, fixed and could not fix, in plain words. */
  readonly notes = computed<UploadNote[]>(() => {
    const columns = this.store.columns();
    const rows = this.store.rowsSig();
    if (!columns.length) return [];
    const notes: UploadNote[] = [];
    const sheet = this.store.sheetName();

    notes.push({
      tone: 'ok',
      text: `Read ${formatCount(rows.length)} ${rows.length === 1 ? 'row' : 'rows'} and ${columns.length} ${
        columns.length === 1 ? 'column' : 'columns'
      }${sheet && this.store.sheetNames().length > 1 ? ` from “${sheet}”` : ''}.`,
      more: 'The first row is used as the column names.',
    });

    const unsafe = columns.filter((column) => column.renamed === 'unsafe');
    if (unsafe.length) {
      notes.push({
        tone: 'wait',
        text: `Renamed ${unsafe.length === 1 ? 'a column' : `${unsafe.length} columns`} so ${
          unsafe.length === 1 ? 'it' : 'they'
        } can go in the message: ${unsafe
          .slice(0, 4)
          .map((column) => `${column.header} → ${column.field}`)
          .join(', ')}${unsafe.length > 4 ? ` and ${unsafe.length - 4} more` : ''}.`,
        more: 'Field names can only use letters, numbers, spaces and underscores.',
      });
    }

    const blank = columns.filter((column) => column.renamed === 'blank');
    if (blank.length) {
      notes.push({
        tone: 'wait',
        text: `${blank.length === 1 ? 'One column has' : `${blank.length} columns have`} no name, so ${
          blank.length === 1 ? 'it is' : 'they are'
        } now ${blank.map((column) => column.field).join(', ')}.`,
      });
    }

    const repeated = columns.filter((column) => column.renamed === 'duplicate');
    if (repeated.length) {
      notes.push({
        tone: 'wait',
        text: `${repeated
          .map((column) => `“${column.header}”`)
          .join(', ')} appeared more than once, so the later ${
          repeated.length === 1 ? 'copy is' : 'copies are'
        } now ${repeated.map((column) => column.field).join(', ')}.`,
      });
    }

    const restored = columns.reduce((sum, column) => sum + column.restored, 0);
    if (restored) {
      notes.push({
        tone: 'ok',
        text: `Read ${formatCount(restored)} long ${restored === 1 ? 'number' : 'numbers'} in full that Excel shows in short form, like 2.67E+11.`,
      });
    }

    const shortened = columns.filter((column) => column.shortened > 0);
    if (shortened.length) {
      const total = shortened.reduce((sum, column) => sum + column.shortened, 0);
      const example = this.shortenedExample(shortened[0].index);
      notes.push({
        tone: 'bad',
        text: `${formatCount(total)} ${total === 1 ? 'value was' : 'values were'} saved in short form${
          example ? `, like ${example}` : ''
        }, so ${total === 1 ? 'its' : 'their'} last digits are lost.`,
        more: 'In Excel, format the column as Text, re-enter or re-export the numbers, then upload the file again.',
      });
    }

    const skipped = this.store.skippedRows();
    if (skipped) {
      notes.push({ tone: 'idle', text: `Left out ${formatCount(skipped)} empty ${skipped === 1 ? 'row' : 'rows'}.` });
    }
    const dropped = this.store.droppedColumns();
    if (dropped) {
      notes.push({ tone: 'idle', text: `Left out ${dropped} empty ${dropped === 1 ? 'column' : 'columns'}.` });
    }
    return notes;
  });

  private shortenedExample(column: number): string {
    for (const row of this.store.rowsSig()) {
      const value = row[column];
      if (value && /e\+?\d+$/i.test(value)) return value;
    }
    return '';
  }

  sheetLabel(index: number): string {
    const name = this.store.sheetNames()[index] ?? '';
    const size = this.store.sheetSizes()[index] ?? 0;
    return size ? `${name} (${formatCount(size)} ${size === 1 ? 'row' : 'rows'})` : `${name} (empty)`;
  }

  // ---- picking a file ---------------------------------------------------------
  choose() {
    this.picker?.nativeElement.click();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isHovering = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    const zone = event.currentTarget as Node | null;
    const into = event.relatedTarget as Node | null;
    if (!zone || !into || !zone.contains(into)) this.isHovering = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isHovering = false;
    if (event.dataTransfer?.files?.[0]) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      this.handleFile(input.files[0]);
    }
    // allow re-selecting the same file
    input.value = '';
  }

  onSheetChange(event: Event) {
    const name = (event.target as HTMLSelectElement).value;
    this.errorMessage = this.store.selectSheet(name);
    this.announcement = this.errorMessage ? '' : (this.notes()[0]?.text ?? '');
  }

  private handleFile(file: File) {
    if (!isSpreadsheetFile(file.name)) {
      this.errorMessage = `“${file.name}” isn’t a spreadsheet. Upload a CSV, Excel (.xlsx, .xls) or OpenDocument (.ods) file.`;
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.errorMessage = `“${file.name}” is ${formatFileSize(file.size, 1)}. Upload a file under ${formatFileSize(
        MAX_FILE_BYTES,
        0
      )}, or split it into smaller files.`;
      return;
    }
    this.errorMessage = '';
    this.loading = true;
    this.readingName = file.name;
    this.announcement = `Reading ${file.name}…`;
    // Defer the heavy parse so the loading state paints first.
    setTimeout(() => this.parseFile(file), 0);
  }

  private parseFile(file: File): void {
    const reader = new FileReader();
    reader.onerror = () => {
      this.loading = false;
      this.announcement = '';
      this.errorMessage = `We couldn’t open “${file.name}”. Check it isn’t open in another app or password-protected, then try again.`;
    };
    reader.onload = (e) => {
      try {
        this.errorMessage = this.store.loadFile(file, e.target?.result as ArrayBuffer);
      } catch {
        this.errorMessage = `We couldn’t read “${file.name}” as a spreadsheet. Save it again as CSV or .xlsx and upload the new file.`;
      }
      this.loading = false;
      // Errors are announced by their own alert; a good read by its first note.
      this.announcement = this.errorMessage ? '' : (this.notes()[0]?.text ?? '');
    };
    reader.readAsArrayBuffer(file);
  }

  get canContinue(): boolean {
    return this.store.hasRows() && !this.loading;
  }

  continue() {
    if (this.canContinue) {
      this.next.emit();
    }
  }
}
